import React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import OverviewPage from "./pages/OverviewPage";
import AiAgentsPage from "./pages/ai-agents/AiAgentsPage";
import OrdersPage from "./pages/orders/OrdersPage";
import OrderDetailWrapper from "./pages/orders/OrderDetailWrapper";
import ConversationsPage from "./pages/conversations/ConversationsPage";
import ConversationDetailWrapper from "./pages/conversations/ConversationDetailWrapper";
import VoiceCallsPage from "./pages/analyze/VoiceCallsPage";
import ReportsPage from "./pages/analyze/ReportsPage";
import CustomersPage from "./pages/customers/CustomersPage";
import CustomerDetailWrapper from "./pages/customers/CustomerDetailWrapper";
import SupportPage from "./pages/SupportPage";
import SettingsPage from "./pages/SettingsPage";
import OnlinePaymentsSettingsPage from "./pages/OnlinePaymentsSettingsPage";
import AgentTranslationsSettingsPage from "./pages/AgentTranslationsSettingsPage";
import AccountPage from "./pages/AccountPage";
import CampaignsPage from "./pages/campaigns/CampaignsPage";
import CampaignDetailWrapper from "./pages/campaigns/CampaignDetailWrapper";
import CreateCampaignPage from "./pages/campaigns/CreateCampaignPage";
import DiscountsPage from "./pages/discounts/DiscountsPage";
import DiscountsPageWrapper from "./pages/discounts/DiscountsPageWrapper";
import LinkInBioListPage from "./pages/linkInBio/LinkInBioListPage";
import LinkInBioSettingsPage from "./pages/linkInBio/LinkInBioSettingsPage";
import MetaAdsLayout from "./pages/metaAds/MetaAdsLayout";
import MetaAdsHubPage from "./pages/metaAds/MetaAdsHubPage";
import MetaAdsConnectPage from "./pages/metaAds/MetaAdsConnectPage";
import MetaAdsCreateCampaignPage from "./pages/metaAds/MetaAdsCreateCampaignPage";
import MetaAdsEditAdPage from "./pages/metaAds/MetaAdsEditAdPage";
import MetaAdsEditAdSetPage from "./pages/metaAds/MetaAdsEditAdSetPage";
import MetaAdsEditCampaignPage from "./pages/metaAds/MetaAdsEditCampaignPage";
import MetaAdsCampaignSubmittedPage from "./pages/metaAds/MetaAdsCampaignSubmittedPage";
import MetaAdsPerformancePage from "./pages/metaAds/MetaAdsPerformancePage";
import PageViewTracker from "./PageViewTracker";
import { usePostHog } from "@finitless/shared/hooks";

// Store Manager Pages (migrated from Connect)
import StoresPage from "./pages/menu/stores";
import BusinessAddressSelection from "./pages/menu/stores/BusinessAddressSelection";
import BusinessCreationForm from "./pages/menu/stores/BusinessCreationForm";
import BusinessDetailPage from "./pages/menu/stores/BusinessDetailPage";

// Products Pages (migrated from Connect)
import ProductsIndex from "./pages/products/ProductsIndex";
import ProductsBusinessPage from "./pages/products/ProductsBusinessPage";

// Brands Pages (migrated from Connect)
import BrandsPage from "./pages/brands/BrandsPage";
import BrandDetailPage from "./pages/brands/BrandDetailPage";
import CartRecoveryPage from "./pages/growth/cart-recovery/CartRecoveryPage";
import CartRecoveryDetailWrapper from "./pages/growth/cart-recovery/CartRecoveryDetailWrapper";
import CartRecoveryCreatePage from "./pages/growth/cart-recovery/CartRecoveryCreatePage";

// Delivery Pages (migrated from Connect)
import DeliveryServicesPage from "./pages/delivery";
import DeliveryServiceDetailPage from "./pages/delivery/DeliveryServiceDetailPage";
import DriversManagersPage from "./pages/delivery/DriversManagersPage";
import DriverManagerDetailPage from "./pages/delivery/DriverManagerDetailPage";
import DeliveryGroupsPage from "./pages/delivery/DeliveryGroupsPage";
import CreateDeliveryGroupPage from "./pages/delivery/CreateDeliveryGroupPage";
import DeliveryGroupDetailPage from "./pages/delivery/DeliveryGroupDetailPage";

// Admins Pages (separated from Customers)
import AdminsPage from "./pages/admins/AdminsPage";
import AdminDetailWrapper from "./pages/admins/AdminDetailWrapper";

// Integrations Pages (migrated from Connect)
import POSPage from "./pages/integrations/POSPage";
import MenuImportersPage from "./pages/integrations/MenuImportersPage";
import SalesChannelsPage from "./pages/integrations/SalesChannelsPage";

const LegacyMenuCartRecoveryIdRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/growth/cart-recovery/${id}`} replace />;
};

const AppRouter = () => {
  const { isFeatureEnabled } = usePostHog();
  const stripeConnectPluginEnabled = isFeatureEnabled("stripe_connect_link");
  const campaignsEnabled = isFeatureEnabled("campaigns");
  // const metaAdsEnabled = isFeatureEnabled("meta_ads_enabled");
  const metaAdsEnabled = true;
  // const menuImportersEnabled = isFeatureEnabled("menu_importers_enabled");
  const menuImportersEnabled = true;

  return (
    <>
      <PageViewTracker />
      <Routes>
        <Route path="/" element={<Navigate to="/overview" replace />} />
        <Route path="/overview" element={<OverviewPage />} />
        <Route path="/ai-agents" element={<AiAgentsPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/orders/:id" element={<OrderDetailWrapper />} />
        <Route path="/analyze/conversations" element={<ConversationsPage />} />
        <Route
          path="/analyze/conversations/:id"
          element={<ConversationDetailWrapper />}
        />
        <Route path="/analyze/voice-calls" element={<VoiceCallsPage />} />
        <Route path="/analyze/reports" element={<ReportsPage />} />
        <Route path="/users" element={<CustomersPage />} />
        <Route path="/users/:id" element={<CustomerDetailWrapper />} />
        <Route path="/settings/basic" element={<SettingsPage />} />
        {stripeConnectPluginEnabled ? (
          <Route
            path="/settings/online-payments"
            element={<OnlinePaymentsSettingsPage />}
          />
        ) : (
          <Route
            path="/settings/online-payments"
            element={<Navigate to="/settings/basic" replace />}
          />
        )}
        <Route
          path="/settings/agent-translations"
          element={<AgentTranslationsSettingsPage />}
        />
        <Route path="/settings/admins" element={<AdminsPage />} />
        <Route path="/settings/admins/:id" element={<AdminDetailWrapper />} />
        {campaignsEnabled ? (
          <>
            <Route path="/campaigns" element={<CampaignsPage />} />
            <Route path="/campaigns/new" element={<CreateCampaignPage />} />
            <Route path="/campaigns/:id" element={<CampaignDetailWrapper />} />
          </>
        ) : (
          <>
            <Route
              path="/campaigns"
              element={<Navigate to="/overview" replace />}
            />
            <Route
              path="/campaigns/new"
              element={<Navigate to="/overview" replace />}
            />
            <Route
              path="/campaigns/:id"
              element={<Navigate to="/overview" replace />}
            />
          </>
        )}
        <Route path="/discounts" element={<DiscountsPage />} />
        <Route
          path="/discounts/:promotionId"
          element={<DiscountsPageWrapper />}
        />
        <Route
          path="/growth/link-in-bio/:profileId"
          element={<LinkInBioSettingsPage />}
        />
        <Route path="/growth/link-in-bio" element={<LinkInBioListPage />} />

        <Route
          path="/growth/meta-ads"
          element={
            metaAdsEnabled ? (
              <MetaAdsLayout />
            ) : (
              <Navigate to="/overview" replace />
            )
          }
        >
          <Route index element={<MetaAdsHubPage />} />
          <Route path="connect" element={<MetaAdsConnectPage />} />
          <Route path="create" element={<MetaAdsCreateCampaignPage />} />
          <Route
            path="edit/:campaignId"
            element={<MetaAdsEditCampaignPage />}
          />
          <Route
            path="ad-sets/edit/:adSetId"
            element={<MetaAdsEditAdSetPage />}
          />
          <Route path="ads/edit/:adId" element={<MetaAdsEditAdPage />} />
          <Route path="success" element={<MetaAdsCampaignSubmittedPage />} />
          <Route path="performance" element={<MetaAdsPerformancePage />} />
        </Route>

        <Route path="/support" element={<SupportPage />} />
        <Route path="/account" element={<AccountPage />} />

        {/* Store Manager Routes (migrated from Connect app) */}
        <Route path="/menu/stores" element={<StoresPage />} />
        <Route
          path="/menu/stores/create/address"
          element={<BusinessAddressSelection />}
        />
        <Route
          path="/menu/stores/create/form"
          element={<BusinessCreationForm />}
        />
        <Route path="/menu/stores/:id" element={<BusinessDetailPage />} />
        <Route path="/menu/products" element={<ProductsIndex />} />
        <Route path="/menu/products/:slug" element={<ProductsBusinessPage />} />
        <Route path="/menu/brands" element={<BrandsPage />} />
        <Route path="/menu/brands/:id" element={<BrandDetailPage />} />
        <Route path="/growth/cart-recovery" element={<CartRecoveryPage />} />
        <Route
          path="/growth/cart-recovery/new"
          element={<CartRecoveryCreatePage />}
        />
        <Route
          path="/growth/cart-recovery/:id"
          element={<CartRecoveryDetailWrapper />}
        />
        <Route
          path="/menu/cart-recovery"
          element={<Navigate to="/growth/cart-recovery" replace />}
        />
        <Route
          path="/menu/cart-recovery/new"
          element={<Navigate to="/growth/cart-recovery/new" replace />}
        />
        <Route
          path="/menu/cart-recovery/:id"
          element={<LegacyMenuCartRecoveryIdRedirect />}
        />

        {/* Integrations Routes (migrated from Connect app) */}
        <Route path="/integrations/pos" element={<POSPage />} />
        {menuImportersEnabled ? (
          <Route
            path="/integrations/menu-importers"
            element={<MenuImportersPage />}
          />
        ) : (
          <Route
            path="/integrations/menu-importers"
            element={<Navigate to="/overview" replace />}
          />
        )}
        <Route
          path="/integrations/sales-channels"
          element={<SalesChannelsPage />}
        />
        <Route
          path="/integrations/delivery-services"
          element={<DeliveryServicesPage />}
        />
        <Route
          path="/integrations/delivery-services/:id"
          element={<DeliveryServiceDetailPage />}
        />

        {/* Delivery Routes (migrated from Connect app) */}
        <Route
          path="/delivery/drivers-managers"
          element={<DriversManagersPage />}
        />
        <Route
          path="/delivery/drivers-managers/:id"
          element={<DriverManagerDetailPage />}
        />
        <Route path="/delivery/groups" element={<DeliveryGroupsPage />} />
        <Route
          path="/delivery/groups/create"
          element={<CreateDeliveryGroupPage />}
        />
        <Route
          path="/delivery/groups/:id"
          element={<DeliveryGroupDetailPage />}
        />

        {/* Catch all route - redirect to overview */}
        <Route path="*" element={<Navigate to="/overview" replace />} />
      </Routes>
    </>
  );
};

export default AppRouter;
